import { motion } from "framer-motion";
import { cn } from "@/lib/cn";
import { RadioDot } from "./RadioDot";
import type { GroupSettings as GroupSettingsType } from "@/stores/groupStore";

const SPRING = { type: "spring" as const, stiffness: 300, damping: 30 };

const SECTION_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: SPRING },
};

const MODERATION_LEVELS: {
  value: GroupSettingsType["moderationLevel"];
  label: string;
  description: string;
  recommended?: boolean;
}[] = [
  {
    value: "low",
    label: "Low",
    description: "Light touch -- only block obvious spam",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Balanced -- catch most violations",
    recommended: true,
  },
  {
    value: "high",
    label: "High",
    description: "Strict -- zero tolerance for rule breaking",
  },
];

export function ModerationLevelSection({
  selected,
  onSelect,
}: {
  selected: GroupSettingsType["moderationLevel"];
  onSelect: (level: GroupSettingsType["moderationLevel"]) => void;
}) {
  return (
    <motion.section variants={SECTION_VARIANTS} className="mb-5">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        Moderation Level
      </h2>
      <div className="flex flex-col gap-2">
        {MODERATION_LEVELS.map((level) => {
          const isSelected = selected === level.value;
          return (
            <motion.button
              key={level.value}
              type="button"
              onClick={() => onSelect(level.value)}
              aria-label={`${level.label} moderation level`}
              aria-pressed={isSelected}
              animate={{
                borderColor: isSelected
                  ? "var(--color-primary)"
                  : "var(--color-border)",
              }}
              transition={SPRING}
              className={cn(
                "flex items-start gap-3 rounded-[var(--radius-lg)] border-2 px-4 py-3",
                "text-left transition-colors",
                isSelected
                  ? "bg-[var(--color-primary)]/5"
                  : "bg-[var(--color-surface)]",
              )}
            >
              <RadioDot selected={isSelected} />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isSelected
                        ? "text-[var(--color-primary)]"
                        : "text-[var(--color-text)]",
                    )}
                  >
                    {level.label}
                  </span>
                  {level.recommended && (
                    <span className="rounded-[var(--radius-full)] bg-[var(--color-primary)]/20 px-2 py-0.5 text-[10px] font-medium text-[var(--color-primary)]">
                      Recommended
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  {level.description}
                </p>
              </div>
            </motion.button>
          );
        })}
      </div>
    </motion.section>
  );
}
